import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * TitleTapLayer — Sep 2026 phone-view fix for the Stock Modal.
 *
 * Every informational `title="…"` hover tooltip inside `containerRef`
 * becomes a tap/click popover. Phones have no hover, so all of those
 * explainers were invisible to mobile users. One delegated listener on
 * the modal converts them all — no per-site edits needed.
 *
 * Rules:
 *   • Ignores interactive controls (button / a / input / tabs / role=button)
 *     — their title is an action hint and the click already does something.
 *   • Ignores anything inside an InlinePopover bubble or tagged `data-no-tap-tip`.
 *   • Bubble uses the same clamping as InlinePopover: never wider than
 *     the viewport minus 8px gutters, flips above when short on room,
 *     scrolls internally instead of growing past the screen.
 */
const INTERACTIVE = 'button, a, input, select, textarea, [role="button"], [role="tab"], [role="link"], [role="menuitem"], [contenteditable="true"]';
const PADDING = 8;

export default function TitleTapLayer({ containerRef }) {
  const [tip, setTip] = useState(null); // { text, el }
  const [pos, setPos] = useState(null);
  const bubbleRef = useRef(null);

  const updatePosition = useCallback(() => {
    const el = tip?.el;
    if (!el || !el.isConnected) { setTip(null); return; }
    const rect = el.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = window.innerHeight;
    const w = Math.min(300, Math.max(160, vw - PADDING * 2));
    let left = Math.round(rect.left + rect.width / 2 - w / 2);
    if (left < PADDING) left = PADDING;
    if (left + w > vw - PADDING) left = vw - w - PADDING;
    const belowSpace = vh - rect.bottom - PADDING * 2;
    const aboveSpace = rect.top - PADDING * 2;
    const flipUp = belowSpace < 160 && aboveSpace > belowSpace;
    const top = flipUp ? rect.top - PADDING : rect.bottom + PADDING;
    const maxHeight = Math.max(110, Math.min(360, flipUp ? aboveSpace : belowSpace));
    setPos({ left, top, flipUp, width: w, maxHeight });
  }, [tip]);

  useEffect(() => {
    const root = containerRef?.current;
    if (!root) return undefined;
    const onClick = (e) => {
      if (e.defaultPrevented) return;
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-inline-popover-bubble], [data-no-tap-tip], [data-title-tap-bubble]')) return;
      const titled = target.closest('[title]');
      if (!titled || !root.contains(titled)) return;
      const text = (titled.getAttribute('title') || '').trim();
      if (!text) return;
      const interactive = target.closest(INTERACTIVE);
      if (interactive && (interactive === titled || interactive.contains(titled) || titled.contains(interactive))) return;
      setTip((cur) => (cur?.el === titled ? null : { text, el: titled }));
    };
    root.addEventListener('click', onClick);
    return () => root.removeEventListener('click', onClick);
  }, [containerRef]);

  useLayoutEffect(() => {
    if (!tip) { setPos(null); return undefined; }
    updatePosition();
    const handler = () => updatePosition();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [tip, updatePosition]);

  useEffect(() => {
    if (!tip) return undefined;
    const onDoc = (e) => {
      if (bubbleRef.current?.contains(e.target) || tip.el?.contains(e.target)) return;
      setTip(null);
    };
    const onKey = (e) => { if (e.key === 'Escape') setTip(null); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc, { passive: true });
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [tip]);

  if (!tip || !pos) return null;
  return createPortal(
    <div
      ref={bubbleRef}
      role="dialog"
      data-title-tap-bubble
      data-testid="title-tap-bubble"
      className="fixed z-[200] rounded-lg shadow-xl border border-border bg-popover text-popover-foreground px-3 py-2 text-[11px] leading-snug overflow-y-auto overscroll-contain"
      style={{
        left: pos.left,
        top: pos.flipUp ? undefined : pos.top,
        bottom: pos.flipUp ? window.innerHeight - pos.top : undefined,
        width: pos.width,
        maxWidth: 'calc(100vw - 16px)',
        maxHeight: pos.maxHeight,
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        whiteSpace: 'pre-line',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {tip.text}
    </div>,
    document.body,
  );
}
